/**
 * MS-591 — «عرض الاتفاقية الموقّعة»: the read-only agreement page the confirmation email links to.
 *
 * Backend: MS-581. Everything on this page is server-supplied — the title, the body with its
 * placeholders already substituted, and both halves of the acceptance timestamp. The page picks
 * no version, merges no booking data and formats no dates; what it shows is the stamped snapshot
 * of what the guest actually agreed to, which stays readable even if the host later replaces or
 * deletes every published template.
 *
 * Read-only is structural, not cosmetic: `useSignedAgreement` exposes no accept mutation, so
 * there is nothing on this page that could reach the consent record even if a control were added
 * here by mistake.
 *
 * The route is public — the MS-541 access token in the URL is the credential. Mobile-first,
 * because this opens from an email on a phone.
 */
import useBreakPoinst from "@hooks/useBreakPoinst";
import { useParams } from "react-router-dom";
import {
  PaymentPageShell,
  PaymentPageLogo,
  SignedAgreementCard,
  SignedAgreementBanner,
  SignedAgreementTitle,
  SignedAtText,
  AgreementSignedPill,
  AgreementHeading,
  AgreementParagraph,
  AgreementStateText,
} from "./styles";
import { Logo } from "../../utils/image-consts";
import { useSignedAgreement } from "@services";
import { parseAgreementBody } from "./agreementBody";
import { signedAtLine } from "./signedAt";

const SignedAgreementPage = () => {
  const { isPhone } = useBreakPoinst();
  const { token } = useParams();
  const { document: agreement, isLoading, hasError } = useSignedAgreement(token ?? "");

  // An absent token is the same dead end as a rejected one — a truncated link from an email
  // client — and must land on the error state, never on an indefinite spinner.
  const failed = hasError || !token;
  const acceptedAt = agreement
    ? signedAtLine(agreement.acceptedAtDate, agreement.acceptedAtTime)
    : null;
  const lines = agreement ? parseAgreementBody(agreement.body) : [];

  return (
    <PaymentPageShell $isPhone={isPhone}>
      <PaymentPageLogo $isPhone={isPhone}>
        <img src={Logo} alt="مسؤول" />
      </PaymentPageLogo>

      <SignedAgreementCard $isPhone={isPhone}>
        {failed ? (
          <AgreementStateText>
            تعذر عرض الاتفاقية. قد يكون الرابط غير صحيح أو منتهي الصلاحية. يرجى التواصل مع
            المضيف.
          </AgreementStateText>
        ) : isLoading || !agreement ? (
          <AgreementStateText>جارٍ تحميل الاتفاقية…</AgreementStateText>
        ) : (
          <>
            {/* Signed status and its timestamp are independent: MS-581 can report a signed
                agreement whose stamped time is missing, and an empty «بتاريخ …» line under a
                green tick reads as a system fault. The pill stands alone in that case. */}
            {agreement.isSigned && (
              <SignedAgreementBanner>
                <AgreementSignedPill>تم التوقيع ✓</AgreementSignedPill>
                {acceptedAt && <SignedAtText>{acceptedAt}</SignedAtText>}
              </SignedAgreementBanner>
            )}

            <SignedAgreementTitle $isPhone={isPhone}>
              {agreement.title}
            </SignedAgreementTitle>

            {lines.map((line) =>
              line.isHeading ? (
                <AgreementHeading key={line.key}>{line.text}</AgreementHeading>
              ) : (
                <AgreementParagraph key={line.key}>{line.text}</AgreementParagraph>
              )
            )}
          </>
        )}
      </SignedAgreementCard>
    </PaymentPageShell>
  );
};

export default SignedAgreementPage;
